import { createSelector } from '@ngrx/store';
import { photoStepIncrement, transformChecklist } from '@shared/*';
import { ChecklistInbound, InProgressChecklist, LegacyTransportType, Stop, TourStopAction } from '@models/*';
import { getAllActions, getCurrentAction, getCurrentActionHasChecklist, getCurrentActionHasCrosscheck, getCurrentStop } from './tour.selectors';
import { getSelectedDepotUnloadActionIds } from './depot.selectors';
import { getBulkSignList, getChecklistPhotoGallerySteps, getChecklists, getChecklistsLoading, getCrosschecksLoading, getInProgressChecklists, getLoadedChecklists, getProtocolPhotosInTransit } from './checklist.selectors';
import { of } from 'rxjs';

export const getSelectedDepotUnloadActions = createSelector(
    getCurrentStop,
    getSelectedDepotUnloadActionIds,
    (stop: Stop, actionIds: number[]) => {
        if (!stop || !actionIds.length) {
            return [];
        }

        return stop.actions.filter(action => actionIds.includes(action.id));
    }
);

export const getCurrentChecklist = createSelector(
    getCurrentAction,
    getChecklists,
    getCurrentActionHasChecklist,
    (action: TourStopAction, checklists: ChecklistInbound[], hasChecklist: boolean) => {
        if (!action || !checklists.length || !hasChecklist) {
            return null;
        }
        const foundChecklist = checklists.find(checklist => checklist.transportInternalId === action.transport.internalId);

        return foundChecklist || null;
    }
);

export const getPrecedingDeliveryChecklist = createSelector(
    getCurrentAction,
    getChecklists,
    getCurrentActionHasCrosscheck,
    (action: TourStopAction, checklists: ChecklistInbound[], isPickUpAction: boolean) => {
        if (!action || !checklists.length || !isPickUpAction) {
            return null;
        }
        const precedingDeliveryChecklist = checklists.find(checklist => {
            return (
                checklist.machineId === action.transport.machine.id &&
                checklist.contractDetailInternalId === action.transport.contractDetail.internalId &&
                checklist.transportType === LegacyTransportType.DELIVERY
            );
        });

        return precedingDeliveryChecklist || null;
    }
);

export const getPrecedingDeliveryChecklists = createSelector(
    getLoadedChecklists,
    (entities) => {
        const checklists =  Object.keys(entities).map(key => entities[key]);
        return checklists.filter( checklist => checklist.protocolStatus === 'completed' && checklist.transportType === LegacyTransportType.DELIVERY)
    }
)


export const getTransformedPrecedingDeliveryChecklist = createSelector(
    getPrecedingDeliveryChecklist,
    precedingDeliveryChecklist => precedingDeliveryChecklist ? transformChecklist(precedingDeliveryChecklist) : null
);

export const getCurrentCrosscheckLoading = createSelector(
    getCurrentAction,
    getCrosschecksLoading,
    getCurrentActionHasCrosscheck,
    (action: TourStopAction, actionCrosscheckIds: number[], hasCrosscheck: boolean) => {
        if (!action || !actionCrosscheckIds.length || !hasCrosscheck) {
            return false;
        }

        return actionCrosscheckIds.includes(action.id);
    });

export const getCurrentChecklistLoading = createSelector(
    getCurrentAction,
    getChecklistsLoading,
    getCurrentActionHasChecklist,
    (action: TourStopAction, actionChecklistIds: number[], hasChecklist: boolean) => {
        if (!action || !actionChecklistIds.length || !hasChecklist) {
            return false;
        }

        return actionChecklistIds.includes(action.id);
    });

export const getCurrentInProgressChecklist = createSelector(
    getCurrentAction,
    getInProgressChecklists,
    (action: TourStopAction, entities: { [id: string]: InProgressChecklist }) => {
        if (!action || !Object.keys(entities).length) {
            return null;
        }

        const transportInternalId = action.transport.internalId;
        return entities[transportInternalId] || null;
    });

export const getCurrentCrosscheckGalleryPhotoStep = createSelector(getPrecedingDeliveryChecklist, getChecklistPhotoGallerySteps,
    (crosscheck, photoSteps) => {
        if (!crosscheck || !crosscheck.transportInternalId) {
            return null;
        }

        const currentPhotoGallerySteps = photoSteps[crosscheck.transportInternalId];

        if (!currentPhotoGallerySteps) {
            return { photos: photoStepIncrement, damagePhotos: photoStepIncrement };
        }

        return currentPhotoGallerySteps;
    });

export const getCurrentChecklistGalleryPhotoStep = createSelector(getCurrentChecklist, getChecklistPhotoGallerySteps,
    (checklist, photoSteps) => {
        if (!checklist || !checklist.transportInternalId) {
            return null;
        }

        const currentPhotoGallerySteps = photoSteps[checklist.transportInternalId];

        if (!currentPhotoGallerySteps) {
            return { photos: photoStepIncrement, damagePhotos: photoStepIncrement };
        }

        return currentPhotoGallerySteps;
    });

export const getMachinesPhotosInTransit = createSelector(
    getProtocolPhotosInTransit,
    getAllActions,
    (
        protocolPhotosInTransit: { [transportId: string]: { totalPhotos: number, currPhotos: number } },
        actions: TourStopAction[],
    ) => {
        const validActionTransportsById = {};
        const actionsById = {};
        for (let action of actions) {
            actionsById[action.id] = action;
            if (action?.transport?.machine) {
                validActionTransportsById[action.transport.internalId] = action.transport;
            }
        }
        const protocolPhotoProgress = [];
        for (let transportId of Object.keys(protocolPhotosInTransit)) {
            const transport = validActionTransportsById[transportId];
            protocolPhotoProgress.push({
                machine: transport?.machine?.fleetNumber || null,
                icon: transport?.machine?.icon || null,
                totalPhotos: protocolPhotosInTransit[transportId].totalPhotos,
                currPhotos: protocolPhotosInTransit[transportId].currPhotos,
            });
        }
        return protocolPhotoProgress;
    });


export const getTSAForBulkSign = createSelector(getCurrentStop, getBulkSignList, ( stop: Stop, bulkSignList: number[]) => {
    const selectedActions = stop.actions.map( action => {
        return bulkSignList.includes(action.id);
    });


    of(selectedActions);
})
